// Step fiche display — one structured card per main pipeline step (MexaScreen chat).
const FICHE_TITLES = {
    project: 'Project Fiche',
    target: 'Target Fiche',
    pdb: 'PDB Selection',
    binding_site: 'Binding Site Fiche',
    peptide_candidates: 'Peptide Candidate Fiche',
    screening: 'Screening Launch',
    docking: 'Docking Launch',
};
const PDB_ROW_LABELS = new Set(['Selected PDB', 'Resolution', 'Method', 'Ligand', 'Status']);
const TARGET_ROW_LABELS = new Set([
    'Target',
    'UniProt',
    'Mechanism',
    'Mutations',
    'Key residues',
    'Cofactors',
    'Status',
]);
export function shouldShowStepFiche(phase) {
    return phase === 'brief_complete' || phase === 'between_steps';
}
const FICHE_PLACEHOLDER_VALUES = new Set([
    '',
    'unknown',
    'n/a',
    'na',
    'none',
    'null',
    '—',
    '-',
]);
function formatFicheValue(value) {
    const trimmed = (value !== null && value !== void 0 ? value : '').trim();
    if (!trimmed || FICHE_PLACEHOLDER_VALUES.has(trimmed.toLowerCase())) {
        return '—';
    }
    return trimmed;
}
function row(label, value) {
    return { label, value: formatFicheValue(value) };
}
function statusRow(complete) {
    return {
        label: 'Status',
        value: complete ? 'Complete' : 'Incomplete',
        kind: 'status',
        status: complete ? 'complete' : 'incomplete',
    };
}
function joinList(items) {
    if (!(items === null || items === void 0 ? void 0 : items.length))
        return '—';
    return items.join(', ');
}
function buildProjectFiche(brief) {
    var _a;
    const confirmed = new Set((_a = brief.confirmed_fields) !== null && _a !== void 0 ? _a : []);
    return {
        fiche_type: 'project',
        title: FICHE_TITLES.project,
        rows: [
            row('Target', brief.target),
            row('Pathology', brief.pathology),
            row('Strategy', brief.strategy),
            row('Constraints', joinList(brief.constraints)),
            statusRow(confirmed.size >= 4),
        ],
    };
}
function buildTargetFiche(fiche) {
    return {
        fiche_type: 'target',
        title: FICHE_TITLES.target,
        rows: [
            row('Target', fiche.target),
            row('UniProt', fiche.uniprot_id),
            row('Mechanism', fiche.mechanism),
            row('Mutations', joinList(fiche.mutations) === '—' ? 'wild-type / none' : joinList(fiche.mutations)),
            row('Key residues', joinList(fiche.key_residues)),
            row('Cofactors', joinList(fiche.cofactors) === '—' ? 'none' : joinList(fiche.cofactors)),
        ],
    };
}
function buildPdbFiche(data) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const snapPdb = (_a = data.state_snapshot) === null || _a === void 0 ? void 0 : _a.pdb_fiche;
    const pdb = {
        pdb_id: (_d = (_c = (_b = data.pdb_fiche) === null || _b === void 0 ? void 0 : _b.pdb_id) !== null && _c !== void 0 ? _c : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.pdb_id) !== null && _d !== void 0 ? _d : data.selected_pdb,
        resolution: (_f = (_e = data.pdb_fiche) === null || _e === void 0 ? void 0 : _e.resolution) !== null && _f !== void 0 ? _f : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.resolution,
        method: (_h = (_g = data.pdb_fiche) === null || _g === void 0 ? void 0 : _g.method) !== null && _h !== void 0 ? _h : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.method,
        ligand: (_k = (_j = data.pdb_fiche) === null || _j === void 0 ? void 0 : _j.ligand) !== null && _k !== void 0 ? _k : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.ligand,
    };
    const rows = [row('Selected PDB', pdb.pdb_id)];
    if ((_l = pdb.resolution) === null || _l === void 0 ? void 0 : _l.trim())
        rows.push(row('Resolution', pdb.resolution));
    if ((_m = pdb.method) === null || _m === void 0 ? void 0 : _m.trim())
        rows.push(row('Method', pdb.method));
    if ((_o = pdb.ligand) === null || _o === void 0 ? void 0 : _o.trim())
        rows.push(row('Ligand', pdb.ligand));
    return {
        fiche_type: 'pdb',
        title: FICHE_TITLES.pdb,
        rows,
    };
}
function filterRowsForFicheType(ficheType, rows) {
    return rows.filter((r) => {
        if (r.kind === 'status')
            return true;
        if (ficheType === 'pdb') {
            return PDB_ROW_LABELS.has(r.label);
        }
        if (ficheType === 'target') {
            return TARGET_ROW_LABELS.has(r.label);
        }
        return true;
    });
}
/** Strip cross-step leakage (e.g. target mutations on PDB fiche from legacy agent payloads). */
function sanitizeIncomingStepFiche(fiche, data) {
    const normalized = Object.assign(Object.assign({}, fiche), { title: fiche.title || FICHE_TITLES[fiche.fiche_type] || 'Step summary', rows: fiche.rows.map(normalizeStepFicheRow) });
    if (normalized.fiche_type === 'pdb') {
        const filtered = filterRowsForFicheType('pdb', normalized.rows);
        const hasPdb = filtered.some((r) => r.label === 'Selected PDB' && r.value !== '—');
        if (!hasPdb || filtered.length <= 1) {
            return buildPdbFiche(data);
        }
        return Object.assign(Object.assign({}, normalized), { rows: filtered });
    }
    if (normalized.fiche_type === 'target') {
        return Object.assign(Object.assign({}, normalized), { rows: filterRowsForFicheType('target', normalized.rows) });
    }
    return normalized;
}
function buildBindingSiteFiche(site, selectedPdb) {
    var _a, _b;
    const centroid = ((_a = site.centroid) === null || _a === void 0 ? void 0 : _a.length) ? `[${site.centroid.join(', ')}]` : '—';
    return {
        fiche_type: 'binding_site',
        title: FICHE_TITLES.binding_site,
        rows: [
            row('PDB', (_b = site.pdb_id) !== null && _b !== void 0 ? _b : selectedPdb),
            row('Pocket ID', site.pocket_id),
            row('Score', site.score != null ? String(site.score) : '—'),
            row('Residues', joinList(site.residues)),
            row('Centroid', centroid),
        ],
    };
}
function buildPeptideCandidatesFiche(candidates, selectedPdb) {
    const rows = [
        row('Selected PDB', selectedPdb),
        row('Candidates', candidates.length ? String(candidates.length) : '0'),
    ];
    candidates.slice(0, 8).forEach((c) => {
        var _a, _b, _c;
        rows.push(row((_a = c.id) !== null && _a !== void 0 ? _a : 'Candidate', `${(_b = c.sequence) !== null && _b !== void 0 ? _b : '—'} · score ${(_c = c.docking_score) !== null && _c !== void 0 ? _c : '—'}`));
    });
    if (candidates.length > 8) {
        rows.push(row('…', `${candidates.length - 8} more`));
    }
    return {
        fiche_type: 'peptide_candidates',
        title: FICHE_TITLES.peptide_candidates,
        rows,
    };
}
/** Prefer unified step_fiche from LangGraph; legacy fields as fallback until agent is updated. */
export function resolveStepFiche(data) {
    var _a, _b, _c, _d, _e, _f, _g;
    const phase = (_a = data.phase) !== null && _a !== void 0 ? _a : 'collecting_brief';
    if (!shouldShowStepFiche(phase))
        return null;
    if ((_c = (_b = data.step_fiche) === null || _b === void 0 ? void 0 : _b.rows) === null || _c === void 0 ? void 0 : _c.length) {
        return sanitizeIncomingStepFiche(data.step_fiche, data);
    }
    const step = (_d = data.current_step) !== null && _d !== void 0 ? _d : '';
    if (phase === 'brief_complete' && data.project_brief) {
        return buildProjectFiche(data.project_brief);
    }
    if (phase === 'between_steps') {
        if (step === 'target_biology' && data.target_fiche) {
            return buildTargetFiche(data.target_fiche);
        }
        if (step === 'pdb_selection' && (data.selected_pdb || ((_e = data.pdb_fiche) === null || _e === void 0 ? void 0 : _e.pdb_id))) {
            return buildPdbFiche(data);
        }
        if (step === 'receptor_preparation' && !((_f = data.state_snapshot) === null || _f === void 0 ? void 0 : _f.receptor_id)) {
            return buildPdbFiche(data);
        }
        if (step === 'binding_site' && data.binding_site) {
            return buildBindingSiteFiche(data.binding_site, data.selected_pdb);
        }
        if (step === 'peptide_design' && ((_g = data.candidates) === null || _g === void 0 ? void 0 : _g.length)) {
            return buildPeptideCandidatesFiche(data.candidates, data.selected_pdb);
        }
    }
    return null;
}
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function parseStatusValue(value) {
    const v = value.trim().toLowerCase();
    if (v === 'complete' || v === 'completed' || v === 'done' || v === 'ok') {
        return 'complete';
    }
    return 'incomplete';
}
function normalizeStepFicheRow(r) {
    var _a;
    if (r.kind === 'status' || r.label.toLowerCase() === 'status') {
        const status = (_a = r.status) !== null && _a !== void 0 ? _a : parseStatusValue(r.value);
        return Object.assign(Object.assign({}, r), { kind: 'status', status, value: status === 'complete' ? 'Complete' : 'Incomplete' });
    }
    return r;
}
function renderStatusBadge(status) {
    const isComplete = status === 'complete';
    const label = isComplete ? 'Complete' : 'Incomplete';
    const icon = isComplete ? '✓' : '!';
    return `<span class="chat-step-fiche-status-badge chat-step-fiche-status-${status}" role="status" aria-label="Status: ${label}">
    <span class="chat-step-fiche-status-icon" aria-hidden="true">${icon}</span>
    ${escapeHtml(label)}
  </span>`;
}
function renderFicheRow(r) {
    const normalized = normalizeStepFicheRow(r);
    if (normalized.kind === 'status') {
        return '';
    }
    return `<div class="chat-step-fiche-row">
    <span>${escapeHtml(normalized.label)}</span>
    <strong>${escapeHtml(normalized.value)}</strong>
  </div>`;
}
function renderStatusFooter(rows) {
    var _a;
    const statusRow = rows.map(normalizeStepFicheRow).find((r) => r.kind === 'status');
    if (!statusRow)
        return '';
    const badge = renderStatusBadge((_a = statusRow.status) !== null && _a !== void 0 ? _a : parseStatusValue(statusRow.value));
    return `<div class="chat-step-fiche-status-footer">${badge}</div>`;
}
export function appendStepFiche(fiche) {
    const chatHistory = document.getElementById('chat-history');
    if (!chatHistory)
        return;
    // Replace the latest card of the same type so brief edits refresh in place.
    const prev = chatHistory.querySelector(`.chat-step-fiche-${fiche.fiche_type}:last-of-type`);
    if (prev)
        prev.remove();
    const rowsHtml = fiche.rows.map(renderFicheRow).join('');
    const statusFooter = renderStatusFooter(fiche.rows);
    const block = document.createElement('div');
    block.className = `chat-step-fiche chat-step-fiche-${fiche.fiche_type}`;
    block.dataset.ficheType = fiche.fiche_type;
    block.innerHTML = `
    <div class="chat-step-fiche-title">${escapeHtml(fiche.title)}</div>
    <div class="chat-step-fiche-body">${rowsHtml}</div>
    ${statusFooter}
  `;
    chatHistory.appendChild(block);
    chatHistory.scrollTop = chatHistory.scrollHeight;
}
export function stepFicheDedupKey(data, fiche) {
    var _a, _b;
    const contentKey = fiche.rows
        .map((r) => { var _a; return `${r.label}:${r.value}:${(_a = r.status) !== null && _a !== void 0 ? _a : ''}`; })
        .join('|');
    return `${(_a = data.project_id) !== null && _a !== void 0 ? _a : ''}:${(_b = data.current_step) !== null && _b !== void 0 ? _b : ''}:${fiche.fiche_type}:${contentKey}`;
}
